import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, StatCard, StatRow, ToggleGroup, Pagination, StatusBadge, cardStyle, sectionTitleStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, greenText, redText, formatDate } from './shared';

export default function RevenuePage() {
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.getRevenueSummary>> | null>(null);
  const [trend, setTrend] = useState<Awaited<ReturnType<typeof api.getMrrTrend>> | null>(null);
  const [subs, setSubs] = useState<Awaited<ReturnType<typeof api.getSubscriptionBreakdown>> | null>(null);
  const [payments, setPayments] = useState<Awaited<ReturnType<typeof api.getPayments>> | null>(null);
  const [trendRange, setTrendRange] = useState('6M');
  const [paymentPage, setPaymentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, t, sub, p] = await Promise.all([
        api.getRevenueSummary(),
        api.getMrrTrend(trendRange.toLowerCase()),
        api.getSubscriptionBreakdown(),
        api.getPayments(paymentPage),
      ]);
      setSummary(s); setTrend(t); setSubs(sub); setPayments(p);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [trendRange, paymentPage]);

  if (loading && !summary) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const totalPaying = (subs?.proMonthly ?? 0) + (subs?.proAnnual ?? 0) + (subs?.enterprise ?? 0);

  return (
    <div>
      <PageHeader title="Revenue" subtitle={`Stripe billing overview · ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>
            Export CSV
          </button>
          <button style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>
            Open Stripe
          </button>
        </div>
      </PageHeader>

      <StatRow>
        <StatCard label="MRR" value={`$${summary?.mrr.toLocaleString() ?? '0'}`} accent={<span style={{ ...greenText, fontSize: 14 }}>+8.2%</span>} sub={`vs $${Math.round((summary?.mrr ?? 0) * 0.92).toLocaleString()} last month`} />
        <StatCard label="ARR" value={`$${summary?.arr.toLocaleString() ?? '0'}`} sub="Projected annual" />
        <StatCard label="CHURN RATE" value={`${summary?.churnRate ?? 0}%`} accent={<span style={{ ...greenText, fontSize: 14 }}>+0.8%</span>} sub="2 cancellations this month" />
        <StatCard label="FAILED PAYMENTS" value={String(summary?.failedPayments.count ?? 0)} accent={<span style={{ ...redText, fontSize: 14 }}>${summary?.failedPayments.amount ?? 0} at risk</span>} sub="1 retry scheduled" />
      </StatRow>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {/* MRR Trend */}
        <div style={{ ...cardStyle, flex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={sectionTitleStyle}>MRR Trend</span>
            <ToggleGroup options={['3M', '6M', '1Y']} value={trendRange} onChange={setTrendRange} />
          </div>
          {trend && trend.length > 0 ? (() => {
            const max = Math.max(...trend.map(t => t.mrr), 1);
            const yTicks = [0, 1000, 2000, 3000, 4000, 5000].filter(t => t <= Math.ceil(max / 1000) * 1000 + 1000);
            const yMax = yTicks[yTicks.length - 1] || max;
            return (
              <div style={{ display: 'flex', height: 200 }}>
                {/* Y-axis */}
                <div style={{ display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between', paddingBottom: 20, marginRight: 8, width: 30 }}>
                  {yTicks.map((t) => (
                    <span key={t} style={{ fontSize: 11, color: '#8B7D6B', textAlign: 'right' }}>{t === 0 ? '$0' : `$${t / 1000}k`}</span>
                  ))}
                </div>
                {/* Bars */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  {trend.map((d, i) => {
                    const h = Math.max((d.mrr / yMax) * 180, 4);
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: '70%', height: h, backgroundColor: '#D46A3A', borderRadius: 3, opacity: 0.5 + (i / trend.length) * 0.5 }} />
                        <span style={{ fontSize: 11, color: '#8B7D6B' }}>{d.month?.split('-')[1] ? ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(d.month.split('-')[1])] : d.month ?? '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })() : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8B7D6B' }}>No data</div>
          )}
        </div>

        {/* Active Subscriptions */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Active Subscriptions</span>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <SubRow label="Pro Monthly" count={subs?.proMonthly ?? 0} color="#D46A3A" total={totalPaying} sub={`$${(subs?.proMonthly ?? 0) * 19}/mo · $19 each`} />
            <SubRow label="Pro Annual" count={subs?.proAnnual ?? 0} color="#D46A3A" total={totalPaying} sub={`$${(subs?.proAnnual ?? 0) * 152}/yr · $152 each`} />
            <SubRow label="Enterprise" count={subs?.enterprise ?? 0} color="#8B5E3C" total={totalPaying} sub={`$${(subs?.enterprise ?? 0) * 199}/mo · $199 each`} />
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #F0EBE4', paddingTop: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Total paying</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#D46A3A' }}>{totalPaying} users</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Payments */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={sectionTitleStyle}>Recent Payments</span>
          <span style={{ fontSize: 13, color: '#D46A3A', fontWeight: 500, cursor: 'pointer' }}>View all</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>User</th>
              <th style={tableHeaderStyle}>Amount</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Date</th>
              <th style={tableHeaderStyle}>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {payments?.payments.map((p) => (
              <tr key={p.paymentId}>
                <td style={tableCellStyle}>{typeof p.userId === 'object' ? p.userId.displayName : '—'}</td>
                <td style={{ ...tableCellStyle, fontFamily: '"JetBrains Mono", monospace' }}>${(p.amount / 100).toFixed(2)}</td>
                <td style={tableCellStyle}><StatusBadge status={p.status} /></td>
                <td style={{ ...tableCellStyle, color: '#8B7D6B' }}>{p.paidAt ? formatDate(p.paidAt) : formatDate(p.createdAt)}</td>
                <td style={tableCellStyle}>{p.invoiceUrl ? <a href={p.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#D46A3A', fontSize: 13, textDecoration: 'none' }}>inv_...</a> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments && <Pagination page={payments.page} totalPages={payments.totalPages} total={payments.total} label="payments" onPageChange={setPaymentPage} />}
      </div>
    </div>
  );
}

function SubRow({ label, count, color, total, sub }: { label: string; count: number; color: string; total: number; sub: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{count}</span>
      </div>
      <div style={{ height: 6, backgroundColor: '#F0EBE4', borderRadius: 3, marginBottom: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: '#8B7D6B' }}>{sub}</span>
    </div>
  );
}
